import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  Box,
  CircularProgress,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { PanelGroup } from "react-resizable-panels";
import ContentPanel from "./ContentPanel/ContentPanel";
import MobileContentPanel from "./ContentPanel/MobileContentPanel";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import useMetadata from "../../hooks/useMetadata";
import StyledPanelResizeHandle from "components/StyledPanelResizeHandle/StyledPanelResizeHandle";
import {
  BASE_PATHS,
  ROUTES,
  SUB_PATHS,
} from "app/utils/constants/routeConstants";
import useAdmin from "app/routes/middleware/Admin/useAdmin";
import ContentHeader from "components/ContentHeader/ContentHeader";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import useProblemDetails from "app/hooks/useProblemDetails";
import CodeAndTestPanel from "./CodeTestPanel/CodeTestPanel";
import MobileCodeAndTestPanel from "./CodeTestPanel/MobileCodeAndTestPanel";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import JumboCustomizerButton from "app/shared/JumboCustomizer/JumboCustomizerButton";
import useMetaTitle from "app/hooks/useMetaTitle";
import ErrorView from "components/ErrorView";

const ProblemDetailPage = () => {
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [showSubmissionPanel, setShowSubmissionPanel] = useState(false);
  const [codeTabValue, setCodeTabValue] = useState(0);
  const [contentTabValue, setContentTabValue] = useState(0);
  const { name, id } = useParams();
  const navigate = useNavigate();
  const { metadata } = useMetadata();
  const isAdmin = useAdmin();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  // This ref will help us track if this is the very first load or a subsequent one.
  const isInitialLoad = useRef(true);

  const { problemData, loading, error } = useProblemDetails({
    name,
    id,
  });

  useMetaTitle({
    title: problemData?.defaultTitle,
  });

  useEffect(() => {
    setShowSubmissionPanel(false);
    setCodeTabValue(0);
    setContentTabValue(0);
    setSelectedSubmission(null);
  }, [name, id]);

  // Track loading state to manage initial load
  useEffect(() => {
    if (!loading && problemData) {
      isInitialLoad.current = false;
    }
  }, [loading, problemData]);

  // Determine if in admin context
  const isAdminContext = location.pathname.startsWith(
    BASE_PATHS.ADMIN_PROBLEMS
  );

  const addRoute = isAdminContext
    ? ROUTES.ADMIN_PROBLEMS
    : BASE_PATHS.ALGOS_AND_DS;

  const handleSubmissionSelect = useCallback((submission) => {
    setSelectedSubmission(submission);
    setShowSubmissionPanel(true);
    setCodeTabValue(1); // Switch to Submission tab
  }, []);

  const handleAddProblem = useCallback(() => {
    navigate(ROUTES.ADMIN_PROBLEMS_ADD);
  }, [navigate]);

  const handleUpdateProblem = useCallback(() => {
    if (problemData?.id) {
      navigate(
        `${BASE_PATHS.ADMIN_PROBLEMS}/${SUB_PATHS.UPDATE_PROBLEM}/${problemData.id}`
      );
    }
  }, [navigate, problemData]);

  const handleBack = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleAction = useCallback(
    ({ id: actionId }) => {
      const actions = {
        back: handleBack,
        add: handleAddProblem,
        update: handleUpdateProblem,
      };
      actions[actionId]?.();
    },
    [handleAddProblem, handleUpdateProblem, handleBack]
  );

  const handlePreviousProblem = useCallback(() => {
    navigate(
      `${addRoute}/${
        isAdminContext ? problemData.previous?.id : problemData.previous?.name
      }`
    );
  }, [navigate, problemData, addRoute, isAdminContext]);

  const handleNextProblem = useCallback(() => {
    navigate(
      `${addRoute}/${
        isAdminContext ? problemData.next?.id : problemData.next?.name
      }`
    );
  }, [navigate, problemData, addRoute, isAdminContext]);

  const renderContent = () => {
    // If there is an error, always show the error view.
    if (error) {
      return (
        <ErrorView
          error={error}
          onBack={() => navigate("/")}
          backButtonLabel="Go to Home"
        />
      );
    }

    // Show the full-screen loader ONLY on the initial load.
    if (loading && isInitialLoad.current) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
            p: 3,
            gap: 2,
          }}
        >
          <CircularProgress size={40} />
          <Typography variant="body1" color="text.secondary">
            Loading problem details...
          </Typography>
        </Box>
      );
    }

    // If we have data, render the layout.
    if (problemData) {
      const codeAndTestProps = {
        problemData,
        selectedSubmission,
        setSelectedSubmission,
        showSubmissionPanel,
        setShowSubmissionPanel,
        codeTabValue,
        setCodeTabValue,
      };
      const contentPanelProps = {
        problemData,
        loading,
        metadata,
        onSubmissionSelect: handleSubmissionSelect,
        value: contentTabValue,
        setValue: setContentTabValue,
      };

      const NavigationButtons = () => (
        <>
          {problemData?.previous !== null && (
            <JumboCustomizerButton
              icon={<ArrowBackIcon />}
              onClick={handlePreviousProblem}
              position="left"
              ariaLabel="Previous Problem"
              sx={{
                bottom: 100,
              }}
            />
          )}
          {problemData?.next !== null && (
            <JumboCustomizerButton
              icon={<ArrowForwardIcon />}
              onClick={handleNextProblem}
              position="right"
              ariaLabel="Next Problem"
              sx={{
                bottom: 100,
              }}
            />
          )}
        </>
      );

      return (
        <>
          {isMobile ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
                overflow: "auto",
                boxSizing: "border-box",
              }}
            >
              <Box sx={{ flexShrink: 0, overflow: "auto", minHeight: 200 }}>
                <MobileContentPanel {...contentPanelProps} />
              </Box>
              <Box
                sx={{
                  flexGrow: 1,
                  display: "flex",
                  flexDirection: "column",
                  overflow: "auto",
                  minHeight: 400,
                }}
              >
                <MobileCodeAndTestPanel {...codeAndTestProps} />
              </Box>
            </Box>
          ) : (
            <PanelGroup
              direction="horizontal"
              style={{ width: "100%", height: "100%", boxSizing: "border-box" }}
            >
              <ContentPanel {...contentPanelProps} />
              <StyledPanelResizeHandle direction="vertical" />
              <CodeAndTestPanel {...codeAndTestProps} />
            </PanelGroup>
          )}
          <NavigationButtons />
        </>
      );
    }

    // Fallback for no data after the initial load has passed.
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          p: 3,
        }}
      >
        <Typography color="error">No problem data available.</Typography>
      </Box>
    );
  };

  return (
    <JumboCardQuick
      title={
        <ContentHeader
          isAdmin={isAdmin}
          onAction={handleAction}
          allowedActions={isAdmin ? ["add", "update", "back"] : ["back"]}
        />
      }
      headerSx={{
        borderBottom: 1,
        borderColor: "divider",
        "& .MuiCardHeader-action": { my: -0.75 },
        height: "60px",
        display: "flex",
        alignItems: "center",
        px: 2,
      }}
      wrapperSx={{
        p: 0,
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        "& .MuiCardContent-root:last-child": { paddingBottom: 0 },
      }}
    >
      {renderContent()}
    </JumboCardQuick>
  );
};

export default ProblemDetailPage;
